import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, trackClick } from '@bonusly/analytics';
import { Box, ChevronDownOutline, HStack, Icon, IconButton, Tag, TagCloseButton, TagLabel, Text, VStack } from '@bonusly/design-system';

import { getMoodColor } from '../../../../components/check-ins/emoji';
import { CheckIn } from '../../../../schemas/check-in';

interface RecentCheckInsListProps {
	checkIns: CheckIn[];
	selectedMonth?: string;
	onMonthFilterRemove?: () => void;
	insights?: string;
}

interface CheckInItemProps {
	checkIn: CheckIn;
}

function CheckInItem({ checkIn }: CheckInItemProps) {
	const [isExpanded, setIsExpanded] = useState(false);

	const truncateMessage = (message: string, maxLength: number = 210) => {
		if (message?.length <= maxLength) return message;
		return message?.substring(0, maxLength) + '...';
	};

	const expandable = (checkIn.message?.length ?? 0) > 210;

	return (
		<Box
			px={3}
			py={2}
			borderWidth="1px"
			borderColor="border.default"
			borderRadius="md"
			cursor={expandable ? 'pointer' : 'default'}
			onClick={() => expandable && setIsExpanded(!isExpanded)}
			{...(expandable &&
				trackClick(AnalyticsEvent.CheckIns.OverTimeCheckInExpanded, {
					checkInId: checkIn.id,
					score: checkIn.score,
					expanded: !isExpanded,
				}))}
		>
			<VStack align="start" spacing={1} flex={1}>
				<HStack w="100%">
					<Text bgColor={`${getMoodColor(checkIn.score)}50`} fontSize="sm" fontWeight="semibold" rounded="lg" px={1} py={0.25}>
						{checkIn.score.toFixed(1)}
					</Text>
					<Text fontSize="sm" color="gray.500" flex="1">
						{format(new Date(checkIn.createdAt), 'MMM d, yyyy')}
					</Text>

					{expandable && (
						<IconButton
							aria-label={isExpanded ? 'Show less' : 'Show more'}
							icon={<ChevronDownOutline transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'} transition="transform 0.25s ease-in-out" size="xs" />}
							size="sm"
							variant="ghost"
							position="relative"
							right={-1}
						/>
					)}
				</HStack>

				<Text fontSize="sm" color={checkIn.message ? 'inherit' : 'gray.500'}>
					{checkIn.message ? (isExpanded ? checkIn.message : truncateMessage(checkIn.message)) : 'No comments'}
				</Text>
			</VStack>
		</Box>
	);
}

export function RecentCheckInsList({ checkIns, selectedMonth, onMonthFilterRemove, insights }: RecentCheckInsListProps) {
	const { t } = useTranslation('pulse_check_ins', { keyPrefix: 'history_list' });
	const filteredCheckIns = selectedMonth
		? checkIns.filter((checkIn) => {
				const date = new Date(checkIn.createdAt);

				// Check if selectedMonth is a date range (for 12w period) or regular month (for other periods)
				if (selectedMonth.includes('_')) {
					// Handle bi-weekly date range format: "2024-01-01_2024-01-15"
					const [startDateStr, endDateStr] = selectedMonth.split('_');
					const startDate = parseISO(startDateStr);
					const endDate = parseISO(endDateStr);
					return date >= startDate && date < endDate;
				} else {
					// Handle regular monthly format: "2024-01"
					const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
					return monthKey === selectedMonth;
				}
			})
		: checkIns;

	const sortedCheckIns = [...filteredCheckIns].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

	const getMonthLabel = (monthKey: string) => {
		if (monthKey.includes('_')) {
			// Handle bi-weekly date range format: "2024-01-01_2024-01-15"
			const [startDateStr, endDateStr] = monthKey.split('_');
			const startDate = parseISO(startDateStr);
			const endDate = parseISO(endDateStr);
			return `${format(startDate, 'MMM d')}-${format(endDate, 'd')}`;
		} else {
			// Handle regular monthly format: "2024-01"
			const [year, month] = monthKey.split('-');
			const date = new Date(parseInt(year), parseInt(month) - 1);
			return format(date, 'MMM yyyy');
		}
	};

	return (
		<VStack align="stretch" spacing={4}>
			<HStack justify="space-between" align="center">
				<Text fontSize="md" fontWeight="semibold">
					{selectedMonth ? t('filtered_title') : t('title')}
				</Text>

				{selectedMonth && onMonthFilterRemove && (
					<Tag size="md" variant="solid" bgColor="secondary.bg">
						<TagLabel>{getMonthLabel(selectedMonth)}</TagLabel>
						<TagCloseButton onClick={onMonthFilterRemove} />
					</Tag>
				)}
			</HStack>

			{/* AI Insights */}
			{insights && (
				<Box p={3} bg="purple.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="purple.400">
					<HStack spacing={2} align="start">
						<Icon name="sparkles" color="purple.500" mt={0.5} />
						<Text fontSize="sm" color="purple.800" lineHeight="1.5">
							{insights}
						</Text>
					</HStack>
				</Box>
			)}

			{/* Check-ins List */}
			<VStack align="stretch" spacing={3}>
				{sortedCheckIns.length === 0 ? (
					<Box textAlign="center" py={8}>
						<Text color="gray.500" fontSize="sm">
							{t('no_check_ins_found')}
						</Text>
					</Box>
				) : (
					sortedCheckIns.map((checkIn) => <CheckInItem key={checkIn.id} checkIn={checkIn} />)
				)}
			</VStack>
		</VStack>
	);
}
